
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Toaster as Sonner } from 'sonner';
import { Suspense, lazy } from "react";

// Keep critical pages as static imports
import Index from "./pages/Index";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import GlobalStructuredData from "./components/GlobalStructuredData";
import Redirect from "./components/Redirect";
import CanonicalLink from "./components/CanonicalLink";

// Lazy load non-critical pages to reduce initial bundle size
const Products = lazy(() => import("./pages/Products"));
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const CryptoPOSSystems = lazy(() => import("./pages/CryptoPOSSystems"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const CryptoPOSGuide = lazy(() => import("./pages/blog/CryptoPOSGuide"));
const RestaurantCashDiscountProgram = lazy(() => import("./pages/blog/RestaurantCashDiscountProgram"));
const TransparentRetailProcessing = lazy(() => import("./pages/blog/TransparentRetailProcessing"));
const SmokeShopProcessingExpert = lazy(() => import("./pages/blog/SmokeShopProcessingExpert"));
const CashDiscountGuideFlorida = lazy(() => import("./pages/blog/CashDiscountGuideFlorida"));
const HowToReadMerchantStatement = lazy(() => import("./pages/blog/HowToReadMerchantStatement"));
const CashDiscountVsSurcharging = lazy(() => import("./pages/blog/CashDiscountVsSurcharging"));
const RestaurantSavings18K = lazy(() => import("./pages/blog/RestaurantSavings18K"));
const HighRiskMerchantGuide = lazy(() => import("./pages/blog/HighRiskMerchantGuide"));
const HowCashDiscountWorksFL = lazy(() => import("./pages/blog/HowCashDiscountWorksFL"));
const HiddenFeesProcessing = lazy(() => import("./pages/blog/HiddenFeesProcessing"));
const BestProcessingRestaurants = lazy(() => import("./pages/blog/BestProcessingRestaurants"));
const SmokeShopMerchantServices = lazy(() => import("./pages/blog/SmokeShopMerchantServices"));
const HowToSwitchProcessors = lazy(() => import("./pages/blog/HowToSwitchProcessors"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MerchantApplication = lazy(() => import("./pages/MerchantApplication"));
const DoorDashIntegration = lazy(() => import("./pages/DoorDashIntegration"));
const FreeRateAnalysis = lazy(() => import("./pages/FreeRateAnalysis"));
const CancellationReturnPolicy = lazy(() => import("./pages/CancellationReturnPolicy"));
const MerchantProcessingServices = lazy(() => import("./pages/services/MerchantProcessingServices"));
const RestaurantPaymentProcessing = lazy(() => import("./pages/services/RestaurantPaymentProcessing"));
const SmallBusinessMerchantServices = lazy(() => import("./pages/services/SmallBusinessMerchantServices"));
const PaymentGatewayServices = lazy(() => import("./pages/services/PaymentGatewayServices"));
const POSSystemsServices = lazy(() => import("./pages/services/POSSystemsServices"));
const CashDiscountProgram = lazy(() => import("./pages/services/CashDiscountProgram"));
const HighRiskProcessing = lazy(() => import("./pages/services/HighRiskProcessing"));
const BrowardCountyMerchantProcessing = lazy(() => import("./pages/locations/BrowardCountyMerchantProcessing"));
const ProcessingFeeCalculatorLanding = lazy(() => import("./pages/tools/ProcessingFeeCalculatorLanding"));

// New pages
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const ConvenienceStoreProcessing = lazy(() => import("./pages/industries/ConvenienceStoreProcessing"));
const BarNightclubProcessing = lazy(() => import("./pages/industries/BarNightclubProcessing"));
const RestaurantProcessing = lazy(() => import("./pages/industries/RestaurantProcessing"));
const SmokeShopProcessing = lazy(() => import("./pages/industries/SmokeShopProcessing"));
const RetailProcessing = lazy(() => import("./pages/industries/RetailProcessing"));
const MiamiDadeMerchantProcessing = lazy(() => import("./pages/locations/MiamiDadeMerchantProcessing"));
const PalmBeachMerchantProcessing = lazy(() => import("./pages/locations/PalmBeachMerchantProcessing"));
const HollywoodFLMerchantProcessing = lazy(() => import("./pages/locations/HollywoodFLMerchantProcessing"));
const PompanoBeachMerchantProcessing = lazy(() => import("./pages/locations/PompanoBeachMerchantProcessing"));
const BocaRatonMerchantProcessing = lazy(() => import("./pages/locations/BocaRatonMerchantProcessing"));
const FortLauderdaleMerchantProcessing = lazy(() => import("./pages/locations/FortLauderdaleMerchantProcessing"));
const EquipmentQuote = lazy(() => import("./pages/EquipmentQuote"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const InstagramLanding = lazy(() => import("./pages/InstagramLanding"));
const FacebookLanding = lazy(() => import("./pages/FacebookLanding"));

// Loading component for lazy routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient()

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <GlobalStructuredData />
            <CanonicalLink />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/free-rate-analysis" element={
                <Suspense fallback={<PageLoader />}>
                  <FreeRateAnalysis />
                </Suspense>
              } />
              <Route path="/products" element={
                <Suspense fallback={<PageLoader />}>
                  <Products />
                </Suspense>
              } />
              <Route path="/services" element={
                <Suspense fallback={<PageLoader />}>
                  <Services />
                </Suspense>
              } />
              <Route path="/pos-systems" element={<Redirect to="/products" />} />
              <Route path="/crypto-pos-systems" element={
                <Suspense fallback={<PageLoader />}>
                  <CryptoPOSSystems />
                </Suspense>
              } />
              <Route path="/about" element={
                <Suspense fallback={<PageLoader />}>
                  <About />
                </Suspense>
              } />
              <Route path="/contact" element={
                <Suspense fallback={<PageLoader />}>
                  <Contact />
                </Suspense>
              } />
              <Route path="/blog" element={
                <Suspense fallback={<PageLoader />}>
                  <Blog />
                </Suspense>
              } />
              <Route path="/blog/what-is-a-crypto-pos-system" element={
                <Suspense fallback={<PageLoader />}>
                  <CryptoPOSGuide />
                </Suspense>
              } />
              <Route path="/blog/crypto-pos-guide" element={
                <Suspense fallback={<PageLoader />}>
                  <CryptoPOSGuide />
                </Suspense>
              } />
              <Route path="/blog/restaurant-cash-discount-program" element={
                <Suspense fallback={<PageLoader />}>
                  <RestaurantCashDiscountProgram />
                </Suspense>
              } />
              <Route path="/blog/transparent-retail-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <TransparentRetailProcessing />
                </Suspense>
              } />
              <Route path="/blog/smoke-shop-processing-expert" element={
                <Suspense fallback={<PageLoader />}>
                  <SmokeShopProcessingExpert />
                </Suspense>
              } />
              <Route path="/blog/cash-discount-programs-florida-2026" element={
                <Suspense fallback={<PageLoader />}>
                  <CashDiscountGuideFlorida />
                </Suspense>
              } />
              <Route path="/blog/how-to-read-merchant-statement" element={
                <Suspense fallback={<PageLoader />}>
                  <HowToReadMerchantStatement />
                </Suspense>
              } />
              <Route path="/blog/cash-discount-vs-surcharging-florida" element={
                <Suspense fallback={<PageLoader />}>
                  <CashDiscountVsSurcharging />
                </Suspense>
              } />
              <Route path="/blog/restaurant-savings-18k-annually" element={
                <Suspense fallback={<PageLoader />}>
                  <RestaurantSavings18K />
                </Suspense>
              } />
              <Route path="/blog/high-risk-merchant-processing-guide" element={
                <Suspense fallback={<PageLoader />}>
                  <HighRiskMerchantGuide />
                </Suspense>
              } />
              <Route path="/blog/how-cash-discount-works-florida" element={
                <Suspense fallback={<PageLoader />}>
                  <HowCashDiscountWorksFL />
                </Suspense>
              } />
              <Route path="/blog/hidden-fees-credit-card-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <HiddenFeesProcessing />
                </Suspense>
              } />
              <Route path="/blog/best-processing-restaurants-south-florida" element={
                <Suspense fallback={<PageLoader />}>
                  <BestProcessingRestaurants />
                </Suspense>
              } />
              <Route path="/blog/smoke-shop-merchant-services-florida" element={
                <Suspense fallback={<PageLoader />}>
                  <SmokeShopMerchantServices />
                </Suspense>
              } />
              <Route path="/blog/how-to-switch-credit-card-processors" element={
                <Suspense fallback={<PageLoader />}>
                  <HowToSwitchProcessors />
                </Suspense>
              } />
              <Route path="/auth" element={
                <Suspense fallback={<PageLoader />}>
                  <Auth />
                </Suspense>
              } />
              <Route path="/merchant-application" element={
                <Suspense fallback={<PageLoader />}>
                  <MerchantApplication />
                </Suspense>
              } />
              <Route path="/doordash-integration" element={
                <Suspense fallback={<PageLoader />}>
                  <DoorDashIntegration />
                </Suspense>
              } />
              <Route path="/cancellation-return-policy" element={
                <Suspense fallback={<PageLoader />}>
                  <CancellationReturnPolicy />
                </Suspense>
              } />

              {/* Services */}
              <Route path="/services/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <MerchantProcessingServices />
                </Suspense>
              } />
              <Route path="/services/restaurant-payment-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <RestaurantPaymentProcessing />
                </Suspense>
              } />
              <Route path="/services/small-business-merchant-services" element={
                <Suspense fallback={<PageLoader />}>
                  <SmallBusinessMerchantServices />
                </Suspense>
              } />
              <Route path="/services/payment-gateways" element={
                <Suspense fallback={<PageLoader />}>
                  <PaymentGatewayServices />
                </Suspense>
              } />
              <Route path="/services/pos-systems" element={
                <Suspense fallback={<PageLoader />}>
                  <POSSystemsServices />
                </Suspense>
              } />
              <Route path="/services/cash-discount-program" element={
                <Suspense fallback={<PageLoader />}>
                  <CashDiscountProgram />
                </Suspense>
              } />
              <Route path="/services/high-risk-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <HighRiskProcessing />
                </Suspense>
              } />

              {/* Locations */}
              <Route path="/locations/broward-county-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <BrowardCountyMerchantProcessing />
                </Suspense>
              } />

              {/* Tools */}
              <Route path="/tools/processing-fee-calculator" element={
                <Suspense fallback={<PageLoader />}>
                  <ProcessingFeeCalculatorLanding />
                </Suspense>
              } />

              {/* Case Studies */}
              <Route path="/case-studies" element={
                <Suspense fallback={<PageLoader />}>
                  <CaseStudies />
                </Suspense>
              } />

              {/* Industries */}
              <Route path="/industries/convenience-store-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <ConvenienceStoreProcessing />
                </Suspense>
              } />
              <Route path="/industries/bar-nightclub-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <BarNightclubProcessing />
                </Suspense>
              } />
              <Route path="/industries/restaurant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <RestaurantProcessing />
                </Suspense>
              } />
              <Route path="/industries/smoke-shop-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <SmokeShopProcessing />
                </Suspense>
              } />
              <Route path="/industries/retail-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <RetailProcessing />
                </Suspense>
              } />

              {/* Additional Locations */}
              <Route path="/locations/miami-dade-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <MiamiDadeMerchantProcessing />
                </Suspense>
              } />
              <Route path="/locations/palm-beach-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <PalmBeachMerchantProcessing />
                </Suspense>
              } />
              <Route path="/locations/hollywood-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <HollywoodFLMerchantProcessing />
                </Suspense>
              } />
              <Route path="/locations/pompano-beach-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <PompanoBeachMerchantProcessing />
                </Suspense>
              } />
              <Route path="/locations/boca-raton-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <BocaRatonMerchantProcessing />
                </Suspense>
              } />
              <Route path="/locations/fort-lauderdale-fl/merchant-processing" element={
                <Suspense fallback={<PageLoader />}>
                  <FortLauderdaleMerchantProcessing />
                </Suspense>
              } />

              {/* Privacy Policy */}
              <Route path="/privacy-policy" element={
                <Suspense fallback={<PageLoader />}>
                  <PrivacyPolicy />
                </Suspense>
              } />

              {/* Instagram Landing */}
              <Route path="/instagram" element={
                <Suspense fallback={<PageLoader />}>
                  <InstagramLanding />
                </Suspense>
              } />
              {/* Facebook Landing */}
              <Route path="/facebook" element={
                <Suspense fallback={<PageLoader />}>
                  <FacebookLanding />
                </Suspense>
              } />

              {/* Equipment Quote */}
              <Route path="/equipment-quote" element={
                <Suspense fallback={<PageLoader />}>
                  <EquipmentQuote />
                </Suspense>
              } />

              {/* Legacy URL Redirects */}
              <Route path="/category/valor" element={<Redirect to="/products" />} />
              <Route path="/category/mobile" element={<Redirect to="/products" />} />
              <Route path="/category/online-gateway" element={<Redirect to="/products" />} />
              <Route path="/category/dejavoo" element={<Redirect to="/products" />} />
              <Route path="/category/vivid" element={<Redirect to="/products" />} />
              <Route path="/category/cash-discount-devices" element={<Redirect to="/products" />} />
              <Route path="/category/clover" element={<Redirect to="/products" />} />
              <Route path="/category/card-readers" element={<Redirect to="/products" />} />
              <Route path="/category/terminals" element={<Redirect to="/products" />} />
              <Route path="/product-page/vp100" element={<Redirect to="/products" />} />
              <Route path="/product-page/quickvee-pos-bundle-with-pax-aries-8-1" element={<Redirect to="/products" />} />

              <Route path="*" element={
                <Suspense fallback={<PageLoader />}>
                  <NotFound />
                </Suspense>
              } />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
